import {ObjectNode} from "#template/nodes/object/object-node"
import type {ObjectNodeParameters} from "#template/nodes/object/types"
import type {PositionValue} from "#template/nodes/types"
import type {INodeSceneInteractionsContext, TemplateNodePart, TransformInteraction} from "#template/interactions"
import {Matrix4, Vector3} from "@cm/math"
import {beginTargetableTransformInteraction, isNodeWithMatrixArrayLockedTransformParameter} from "#template/utils/transform-utils"
import {cameraLookAt} from "#template/utils/camera-utils"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {OnCompileContext} from "#template/template-node/types"
import {LiveVector3Value, LiveVector3ValueState} from "#template/runtime-graph/nodes/live-vector3-value"
import type {LiveValueAccessorListEntry} from "#template/runtime-graph/nodes/compile-template"

// A node whose orientation is constrained to look at a `target` point while `targeted` is set
// (camera, area light). Owns the shared scene-interaction behavior: the root and the target marker
// are both draggable, and every transform is routed through `constrainTransformTargetableNode` so
// the look-at constraint holds.
export type TargetableObjectNodeParameters = {
    target: PositionValue
    targeted: boolean
} & ObjectNodeParameters

export type TargetableObjectNodeConstructorParameters = Omit<TargetableObjectNodeParameters, "visible"> & {visible?: boolean}

export abstract class TargetableObjectNode<
    out Parameters extends TargetableObjectNodeParameters = TargetableObjectNodeParameters,
    out ConstructorParameters extends TargetableObjectNodeConstructorParameters = Omit<Parameters, "visible"> & {visible?: boolean},
> extends ObjectNode<Parameters, ConstructorParameters> {
    override canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    override canTransformPart(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): boolean {
        return (part === "target" ? context.transformMode !== "rotate" : true) && isNodeWithMatrixArrayLockedTransformParameter(this)
    }

    override beginTransformInteraction(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): TransformInteraction | undefined {
        if (!isNodeWithMatrixArrayLockedTransformParameter(this)) throw new Error("lockedTransform is not explicit, cannot transform")
        return beginTargetableTransformInteraction(this, part, context)
    }

    override getPartTransform(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): Matrix4 {
        if (part === "target") {
            const {target} = this.parameters
            return Matrix4.translation(target[0], target[1], target[2])
        }

        return context.getTransformAccessor(this)?.getTransform() ?? Matrix4.identity()
    }

    protected targetedTransform(scope: GraphBuilderScope, objectTransform: BuilderInlet<Matrix4>, isInteracting: BuilderInlet<boolean>): BuilderInlet<Matrix4> {
        if (!this.parameters.targeted) return objectTransform
        return scope.pureLambda(
            scope.namedTuple("targetedTransform", objectTransform, this.parameters.target, isInteracting), //Caution, conditional -> stable id
            ([transform, target, isInteracting]) => (isInteracting ? transform : cameraLookAt(transform.getTranslation(), Vector3.fromArray(target))),
            "targetedTransform",
        )
    }

    protected setupTargetLiveValue(scope: GraphBuilderScope, context: OnCompileContext): BuilderOutlet<Vector3> {
        const node = scope.node(LiveVector3Value, {
            state: scope.state(LiveVector3ValueState, "targetLiveValueState"),
            value: Vector3.fromArray(this.parameters.target),
        })
        context.currentTemplate.liveValueAccessorList.push(
            scope.struct<LiveValueAccessorListEntry>("LiveValueAccessorListEntry", {
                templateNode: this,
                accessor: node.accessor,
            }),
        )
        return node.output
    }
}
